import type { ImageContentType } from "@pdfking/compatibility"
import { ExportImage, UrlLoad } from "@pdfking/compatibility"
import { AutoThrow } from "@pdfking/errors"
import { PSOcoated_v3_small as DefaultICC } from "@pdfking/icc"
import { UInt8Collect } from "@pdfking/io"
import { AddMargins, CreateColorConvert, Round } from "@pdfking/math"
import { ZERO_PIXEL } from "@pdfking/math/constants"
import type { PlotColor } from "@pdfking/pdfium"
import { Provision } from "@pdfking/pdfium"
import type { ExtendedColor, PDFPlotFunction } from "@pdfking/renderer-api"
import { CreateContext } from "@pdfking/renderer-api"
import { ContentType } from "@pdfking/structs"
import { RecordMap, WaitPromises } from "@pdfking/tfunc"

// a color with a `spot_name` is an ink of its own, a Separation
const to_plot_color = (color: ExtendedColor): PlotColor =>
  CreateColorConvert<PlotColor>(
    c => ({ ...c, enum: "rgb" }),
    c =>
      color.spot_name ? { ...c, spot_name: color.spot_name, enum: "spot" } : { ...c, enum: "cmyk" }
  )(color)

// The page goes to Rust as authored: Rust converts to pixels, resolves the
// colors, places the media and walks the group tree. Only the context is
// readied here — the streams read and the colors tagged. With a raster Rust
// draws the page it built straight to pixels, soft-proofed through the
// default profile, and never writes the pdf; they are encoded here.
export const PDFPlot: PDFPlotFunction = (pages, partial, raster) =>
  Provision(async function* (api) {
    const { color_map, file_map, layer_map, unit_to_px } = CreateContext(partial)

    const files = await WaitPromises(
      RecordMap(
        entries =>
          WaitPromises(
            RecordMap(
              async ({ stream, matrix }) => ({ bytes: await UInt8Collect(stream), matrix }),
              entries
            )
          ),
        file_map
      )
    )

    // the raster as Rust takes it, with the profile it is proofed through
    const plot_raster = raster && {
      resolution: raster.dpi,
      bleed: raster.bleed,
      icc_handle: AutoThrow(await api.icc_create(await UrlLoad(DefaultICC).then(UInt8Collect))),
    }

    try {
      for (const page of pages) {
        const { bytes, colors, media, transparency } = AutoThrow(
          await api.plot({
            page,
            unit_to_px,
            color_map: RecordMap(to_plot_color, color_map),
            file_map: files,
            layer_map,
            raster: plot_raster,
          })
        )
        if (!plot_raster) {
          yield { stream: bytes, type: ContentType.PDF, colors, transparency }
          continue
        }
        // the bitmap's whole pixels: the trim grown by its bleed, back from
        // the fractions both are reported in
        const grown = AddMargins({ x: ZERO_PIXEL, y: ZERO_PIXEL, ...media.trim }, media.bleed)
        const stream = await ExportImage({
          data: bytes,
          width: Round(grown.width),
          height: Round(grown.height),
        })
        // the encoder picks the platform's default format and tags its blob with it
        yield { stream, type: stream.type as ImageContentType, colors, transparency }
      }
    } finally {
      if (plot_raster) AutoThrow(await api.icc_destroy(plot_raster.icc_handle))
    }
  })
